import React, { useState } from 'react';
import { apiFetch } from '../../api/api';
import './AttachmentForm.css';

export default function DataFormAttachment({
  file,
  tableId,
  tableName,
  onSave,
  onCancel
}) {
  const [attachmentData, setAttachmentData] = useState({
    subject: file?.name || '',
    description: '',
    category: 'General'
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (field, value) => {
    setAttachmentData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);

    try {
      console.log('📤 Creating FormData for DataForm attachment upload...');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('subject', attachmentData.subject || file.name);
      formData.append('description', attachmentData.description);

      // Map tableId to the correct backend field
      let linkField = '';
      if (tableName && tableId) {
        if (tableName.toLowerCase().includes('well')) linkField = 'wellId';
        else if (tableName.toLowerCase().includes('report')) linkField = 'reportId';
        else if (tableName.toLowerCase().includes('bit')) linkField = 'bitId';
      }
      if (linkField) {
        formData.append(linkField, tableId);
      }

      console.log('📡 Sending upload request to /api/attachments...');
      const response = await apiFetch('/api/attachments', {
        method: 'POST',
        body: formData
      });

      console.log('📥 Upload response received:', response);
      
      if (response && (response.attachment || response.success || response.message)) {
        console.log('✅ Attachment saved successfully');
        if (onSave) {
          await onSave({
            ...attachmentData,
            fileName: file.name,
            fileSize: file.size,
            attachmentId: response.attachment?.AttachmentID || response.id
          });
        }
      } else {
        throw new Error(response?.error || 'Upload failed with unexpected response');
      }
    } catch (err) {
      console.error('❌ Failed to save attachment:', err);
      setError(err.message || 'Failed to save attachment');
    } finally {
      setIsSaving(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="attachment-form-overlay">
      <div className="attachment-form">
        <div className="attachment-header">
          <h3>Add Attachment</h3>
          <button className="btn-close" onClick={onCancel}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="attachment-content">
          <div className="file-info">
            <div className="file-icon">📎</div>
            <div className="file-details">
              <div className="file-name">{file.name}</div>
              <div className="file-size">{formatFileSize(file.size)}</div>
            </div>
          </div>

          <div className="attachment-fields">
            <div className="form-field">
              <label className="form-label">
                Subject *
              </label>
              <input
                type="text"
                value={attachmentData.subject}
                onChange={(e) => handleChange('subject', e.target.value)}
                placeholder={file.name}
                className="form-input"
                required
              />
            </div>

            <div className="form-field">
              <label className="form-label">
                Description
              </label>
              <textarea
                value={attachmentData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Optional description of the attachment..."
                className="form-textarea"
                rows={3}
              />
            </div>

            <div className="form-field">
              <label className="form-label">
                Category
              </label>
              <select
                value={attachmentData.category}
                onChange={(e) => handleChange('category', e.target.value)}
                className="form-select"
              >
                <option value="General">General</option>
                <option value="Document">Document</option>
                <option value="Image">Image</option>
                <option value="Data">Data</option>
                <option value="Report">Report</option>
                <option value="Technical">Technical</option>
                <option value="BHA">BHA</option>
              </select>
            </div>

            <div className="form-note">
              <small>
                This attachment will be linked to: <strong>{tableName}</strong> (ID: {tableId})
              </small>
            </div>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="attachment-actions">
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={onCancel}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary" 
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Attachment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 